import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { useGetBankingAccountsQuery } from "../../features/banking/bankingApiSlice";
import { useCreateBookingMutation } from "../../features/banking/bookingsApiSlice";
import { useGetEventQuery } from "../../features/events/eventsApiSlice";
import { setDialog } from "../../features/dialog/dialogSlice";
import FeeCollectionForm from "../../sections/@dashboard/banking/FeeCollectionForm";

const getId = (obj) => String(obj?._id || obj?.id || obj || "");
const roundCurrency = (value) =>
  Math.round((Number(value) || 0) * 100) / 100;

const buildEventReference = (event) => {
  if (!event) return "";
  const date = event?.eventDateBegin
    ? dayjs(event.eventDateBegin).format("DD.MM.YYYY")
    : "";
  return ["Veranstaltungsgebühr", event?.eventName, date]
    .filter(Boolean)
    .join(" ")
    .slice(0, 140);
};

const buildOwnerName = (contract = {}) => {
  const owner = String(contract?.owner || "").trim();
  if (owner) return owner;
  const fullName = `${contract?.firstName || ""} ${contract?.lastName || ""}`.trim();
  return fullName;
};

export default function EventFeeCollectionDialog({ open, onClose, event }) {
  const dispatch = useDispatch();
  const eventId = getId(event);
  const { data: eventDetail, isFetching: loadingEvent } = useGetEventQuery(eventId, {
    skip: !eventId,
  });
  const { data: accounts = [], isLoading: loadingAccounts } =
    useGetBankingAccountsQuery();
  const [createBooking] = useCreateBookingMutation();
  const [selectedAccountId, setSelectedAccountId] = useState("");

  useEffect(() => {
    if (!open || !accounts.length) return;
    const main = accounts.find((acc) => acc.isMain);
    const nextId = getId(main || accounts[0]);
    setSelectedAccountId(nextId);
  }, [open, accounts]);

  const selectedAccount = useMemo(
    () => accounts.find((acc) => getId(acc) === selectedAccountId) || null,
    [accounts, selectedAccountId]
  );

  const creditor = useMemo(() => {
    if (!selectedAccount) return null;
    return {
      creditorName: selectedAccount?.creditorName || "—",
      creditorIBAN: selectedAccount?.creditorIBAN || "",
      creditorBIC: selectedAccount?.creditorBIC || "",
      creditorId: selectedAccount?.creditorId || "",
      remittanceInfo: selectedAccount?.remittanceInfo || "",
    };
  }, [selectedAccount]);

  const mergedEvent = eventDetail || event;
  const pricingOptionAmountById = useMemo(
    () =>
      new Map(
        (Array.isArray(mergedEvent?.pricingOptions) ? mergedEvent.pricingOptions : [])
          .map((option) => [String(option?.optionId || "").trim(), roundCurrency(option?.amount)])
          .filter(([optionId]) => Boolean(optionId))
      ),
    [mergedEvent?.pricingOptions]
  );
  const participants = useMemo(() => {
    const list = Array.isArray(mergedEvent?.participants) ? mergedEvent.participants : [];
    const seen = new Set();
    const mapped = [];

    list.forEach((entry) => {
      const member = entry?.memberId || {};
      const contract = entry?.contractId || {};
      const memberId = getId(member);
      const contractId = getId(contract);
      if (!memberId || !contractId) return;

      const dedupeKey = `${contractId}:${memberId}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);

      mapped.push({
        _id: memberId,
        firstName: member?.firstName || "",
        lastName: member?.lastName || "",
        memberNumber: member?.memberNumber || "",
        status: member?.status || "active",
        standortRef: member?.standortRef || null,
        contractDate: member?.contractDate || entry?.createdAt || null,
        contractRef: {
          _id: contractId,
          owner: buildOwnerName(contract),
          firstName: contract?.firstName || "",
          lastName: contract?.lastName || "",
          iban: contract?.iban || "",
          bic: contract?.bic || "",
        },
        selectedPricingOptionId: String(entry?.selectedPricingOptionId || "").trim(),
        paidAmount:
          typeof entry?.paidAmount === "number" && Number.isFinite(entry.paidAmount)
            ? roundCurrency(entry.paidAmount)
            : undefined,
      });
    });

    return mapped;
  }, [mergedEvent]);

  const feeByMemberId = useMemo(() => {
    const fallbackAmount = roundCurrency(mergedEvent?.feeAmount);
    if (participants.length === 0) return {};
    const map = {};
    participants.forEach((participant) => {
      const memberId = getId(participant);
      if (!memberId) return;
      const selectedPricingOptionId = String(participant?.selectedPricingOptionId || "").trim();
      const optionAmount =
        selectedPricingOptionId && pricingOptionAmountById.has(selectedPricingOptionId)
          ? pricingOptionAmountById.get(selectedPricingOptionId)
          : null;
      const resolvedAmount =
        typeof participant?.paidAmount === "number" && Number.isFinite(participant.paidAmount)
          ? participant.paidAmount
          : optionAmount != null
            ? optionAmount
            : fallbackAmount;
      map[memberId] = roundCurrency(Math.max(0, resolvedAmount));
    });
    return map;
  }, [participants, pricingOptionAmountById, mergedEvent?.feeAmount]);
  const totalPlannedAmount = useMemo(
    () => Object.values(feeByMemberId).reduce((sum, amount) => sum + (Number(amount) || 0), 0),
    [feeByMemberId]
  );

  const referenceText = useMemo(() => buildEventReference(mergedEvent), [mergedEvent]);
  const hasParticipants = participants.length > 0;
  const isFeeBased = Boolean(mergedEvent?.isFeeBased);
  const showForm = !!eventId && !!creditor && hasParticipants && isFeeBased;
  const isBusy = loadingEvent || loadingAccounts;

  const handleEventExport = async (_xml, summary = {}) => {
    const {
      totalAmount,
      collectionDate,
      memberIds = [],
      membersCount: membersCountOverride,
      siteIds = [],
      transactions,
      referenceText,
    } = summary;
    const membersCount =
      Number.isFinite(Number(membersCountOverride)) && membersCountOverride >= 0
        ? Number(membersCountOverride)
        : new Set(memberIds.filter(Boolean)).size;
    if (!collectionDate || !Number.isFinite(Number(totalAmount))) return;

    try {
      await createBooking({
        bookingType: "fee",
        bookingDate: collectionDate,
        membersCount,
        totalSum: Number(totalAmount),
        referenceText,
        transactionsCount: transactions,
        siteIds,
        sepaInfo: {
          source: "event",
          eventId,
          eventName: mergedEvent?.eventName || "",
          eventDateBegin: mergedEvent?.eventDateBegin || null,
          feeAmount: Number(mergedEvent?.feeAmount) || 0,
        },
      }).unwrap();
    } catch (error) {
      console.error("Failed to create booking:", error);
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: {
            errorText:
              error?.data?.message ||
              "Die Buchung konnte nicht gespeichert werden.",
          },
        })
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Einzug Veranstaltungsgebühr</DialogTitle>
      <DialogContent dividers>
        {isBusy && <LinearProgress sx={{ mb: 2 }} />}
        {!eventId && (
          <Typography variant="body2" color="text.secondary">
            Keine Veranstaltung ausgewählt.
          </Typography>
        )}

        {!!eventId && (
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="subtitle1">
                {mergedEvent?.eventName || "Veranstaltung"}
              </Typography>
              {mergedEvent?.eventDateBegin && (
                <Typography variant="body2" color="text.secondary">
                  Datum: {dayjs(mergedEvent.eventDateBegin).format("DD.MM.YYYY")}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Registrierte Teilnehmer: {participants.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Geplanter Gesamtbetrag: {totalPlannedAmount.toFixed(2)} EUR
              </Typography>
            </Box>

            <FormControl sx={{ maxWidth: 420 }}>
              <InputLabel id="event-fee-account-label">Konto für SEPA-Einzug</InputLabel>
              <Select
                labelId="event-fee-account-label"
                label="Konto für SEPA-Einzug"
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
              >
                {accounts.map((acc) => {
                  const id = getId(acc);
                  return (
                    <MenuItem key={id} value={id}>
                      {acc.creditorName || "—"} ({acc.creditorIBAN || "—"})
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Stack>
        )}

        {!!eventId && !isFeeBased && !loadingEvent && (
          <Typography variant="body2" color="text.secondary">
            Diese Veranstaltung ist nicht als kostenpflichtig markiert.
          </Typography>
        )}

        {!!eventId && accounts.length === 0 && !loadingAccounts && (
          <Typography variant="body2" color="text.secondary">
            Keine Bankkonten vorhanden. Bitte hinterlegen Sie ein Konto in den
            Einstellungen.
          </Typography>
        )}

        {!!eventId &&
          accounts.length > 0 &&
          !selectedAccountId &&
          !loadingAccounts && (
            <Typography variant="body2" color="text.secondary">
              Bitte wählen Sie ein Konto für den SEPA-Einzug aus.
            </Typography>
          )}

        {!!eventId && !hasParticipants && !loadingEvent && (
          <Typography variant="body2" color="text.secondary">
            Für diese Veranstaltung sind noch keine Teilnehmer angemeldet.
          </Typography>
        )}

        {showForm && (
          <FeeCollectionForm
            key={`${eventId}-${participants.length}-${selectedAccountId}`}
            members={participants}
            creditor={creditor}
            loading={loadingEvent || loadingAccounts}
            title="SEPA-Lastschrift (Veranstaltungsgebühr)"
            description="Es werden nur registrierte Teilnehmer dieser Veranstaltung angezeigt."
            defaultReferenceText={referenceText}
            defaultOnlyActive={false}
            defaultSelectAll
            defaultFeeByMemberId={feeByMemberId}
            onExport={handleEventExport}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="info">
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EventFeeCollectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  event: PropTypes.object,
};
